import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconSend } from 'src/presentations/components/atom/icons';
import { useNavigate } from 'react-router-dom';
import LogoAskFreya from 'src/assets/images/logo-freya-ask-ai.svg';
import MagicPen from 'src/assets/images/icons/magicpen.svg';
import { useFreyaPopover } from './useFreyaPopover';

interface PopoverMessage {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  isThinking?: boolean;
}

const POPOVER_WIDTH = 380;
const POPOVER_HEIGHT = 420;

const FreyaChatPopover: React.FC = () => {
  const { isOpen: open, hasSession, openPosition, closePopover: onClose, reopenPopover: onReopen, endSession: onEndSession } = useFreyaPopover();
  const showMinimizedBar = hasSession;
  const navigate = useNavigate();
  const [messages, setMessages] = useState<PopoverMessage[]>([{ id: 'welcome', sender: 'bot', text: 'Hi Fauzi, I am Freya' }]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hasUserMessages = messages.some((msg) => msg.sender === 'user');

  // Drag state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  // Initialize position based on openPosition
  useEffect(() => {
    if (open && !initialized.current) {
      if (openPosition === 'center') {
        const x = (window.innerWidth - POPOVER_WIDTH) / 2;
        const y = (window.innerHeight - POPOVER_HEIGHT) / 2;
        setPosition({ x, y });
      } else {
        const sidebarWidth = document.querySelector('[class*="w-[240px]"], [class*="w-[72px]"]')?.getBoundingClientRect().width ?? 260;
        const x = sidebarWidth + 24;
        const y = window.innerHeight - POPOVER_HEIGHT - 24;
        setPosition({ x, y });
      }
      initialized.current = true;
    }
  }, [open, openPosition]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.popover-header')) {
        setIsDragging(true);
        dragOffset.current = {
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        };
        e.preventDefault();
      }
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(window.innerWidth - POPOVER_WIDTH, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - POPOVER_HEIGHT, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSend = () => {
    if (!inputText.trim() || isLoading) return;

    const userMsg: PopoverMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: inputText.trim(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputText('');
    setIsLoading(true);

    const thinkingId = (Date.now() + 1).toString();
    setMessages((prev) => [...prev, { id: thinkingId, sender: 'bot', text: '', isThinking: true }]);

    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === thinkingId
            ? { ...msg, text: 'I can help you with that! For a more detailed view, click "Open in Chat Page" above.', isThinking: false }
            : msg
        )
      );
      setIsLoading(false);
    }, 1500);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenChatPage = () => {
    onClose();
    navigate('/ask-freya');
  };

  const handleEndSession = () => {
    setMessages([{ id: 'welcome', sender: 'bot', text: 'Hi Fauzi, I am Freya' }]);
    setInputText('');
    initialized.current = false;
    onEndSession();
  };

  const minimizedBar =
    showMinimizedBar && !open && hasUserMessages
      ? createPortal(
          <div
            className='fixed bottom-24 z-[999999] flex items-center justify-between gap-12 bg-[#1C2429] rounded-6 px-16 py-8 w-[400px] shadow-[0_4px_16px_rgba(0,0,0,0.3)] cursor-pointer'
            style={{ left: (document.querySelector('[class*="w-[240px]"], [class*="w-[72px]"]')?.getBoundingClientRect().width ?? 260) + 24 }}
          >
            <button type='button' onClick={onReopen} className='flex items-center gap-8 bg-transparent border-none cursor-pointer p-0'>
              <img src={MagicPen} alt='' className='w-24 h-24 rounded-full' />
              <span className='text-white text-14 font-medium'>Ask Freya</span>
            </button>
            <div className='w-1 h-16 bg-white/20' />
            <div className='flex items-center gap-12'>
              <button
                type='button'
                onClick={onReopen}
                className='bg-transparent border-none text-primary text-14 font-medium cursor-pointer p-0 hover:underline'
              >
                Current Session
              </button>
              <button
                type='button'
                onClick={handleEndSession}
                aria-label='End session'
                className='bg-transparent border-none text-white/50 hover:text-white cursor-pointer p-0 transition-colors flex items-center justify-center'
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  if (!open) return minimizedBar;

  return (
    <>
      {createPortal(
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          ref={popoverRef}
          role='dialog'
          aria-label='Freya Chat'
          onMouseDown={handleMouseDown}
          className={`fixed z-[999999] flex flex-col w-[380px] h-[420px] rounded-16 overflow-hidden bg-[#1C2429] text-white shadow-[0_8px_32px_rgba(0,0,0,0.3)] ${
            isDragging ? 'select-none' : ''
          }`}
          style={{ left: position.x, top: position.y }}
        >
          {/* Header - Draggable area */}
          <div className='popover-header flex items-center justify-between p-16 cursor-grab border-b border-white/[0.08] shrink-0'>
            <div className='flex items-center gap-12'>
              <img src={LogoAskFreya} alt='Freya AI' className='w-48 h-48' />
              <div>
                <div className='font-semibold text-14 leading-18'>Freya</div>
                <button
                  type='button'
                  onClick={handleOpenChatPage}
                  className='bg-transparent border-none text-[#00A651] text-12 cursor-pointer p-0 font-medium hover:underline'
                >
                  Open in Chat Page
                </button>
              </div>
            </div>
            <div className='flex items-center gap-4'>
              {hasUserMessages && (
                <button
                  type='button'
                  onClick={onClose}
                  aria-label='Minimize chat'
                  className='p-4 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors'
                >
                  <KeyboardArrowDownIcon fontSize='small' />
                </button>
              )}
              <button
                type='button'
                onClick={handleEndSession}
                aria-label='Close chat'
                className='p-4 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors'
              >
                <CloseIcon fontSize='small' />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className='flex-1 overflow-y-auto p-16 flex flex-col gap-12 scrollbar-hide'>
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-8 items-start ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.sender === 'bot' && (
                  <div className='w-36 h-36 rounded-full flex items-center justify-center shrink-0 text-[#00A651]'>
                    <img src={LogoAskFreya} alt='Freya AI' className='w-36 h-36' />
                  </div>
                )}
                <div
                  className={`max-w-[80%] px-14 py-10 text-13 leading-relaxed text-white ${
                    msg.sender === 'user' ? 'bg-[#00A651] rounded-[12px_12px_4px_12px]' : 'bg-[#383e43] rounded-[12px_12px_12px_4px]'
                  }`}
                >
                  {msg.isThinking ? (
                    <div className='flex gap-4 py-4'>
                      <span className='animate-bounce' style={{ animationDelay: '0ms' }}>
                        ●
                      </span>
                      <span className='animate-bounce' style={{ animationDelay: '150ms' }}>
                        ●
                      </span>
                      <span className='animate-bounce' style={{ animationDelay: '300ms' }}>
                        ●
                      </span>
                    </div>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className='px-16 py-12 border-t border-white/[0.08] shrink-0'>
            <div className='flex items-center gap-8 bg-[#383e43] rounded-6 pl-14 pr-8 py-4'>
              <input
                type='text'
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Ask....'
                disabled={isLoading}
                className='flex-1 bg-transparent border-none outline-none text-white text-13 py-8 placeholder:text-white/40 disabled:cursor-not-allowed'
              />
              <button
                type='button'
                onClick={handleSend}
                disabled={!inputText.trim() || isLoading}
                aria-label='Send message'
                className='p-4 rounded-full text-[#00A651] hover:text-green-70 disabled:text-white/20 transition-colors disabled:cursor-not-allowed'
              >
                <IconSend size={20} />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FreyaChatPopover;
